import React, { useState, useEffect, useMemo } from "react";
import { LoggedRecruiter } from "../../models/LoggedRecruiter";
import { TextInput } from "../Shared/Inputs/TextInput";
import { Sector } from "../../models/Sector";
import { validateCV, validateEmail, validateName, validatePhoneNumber } from "../../helpers/Common";
import { getAllSectors } from "../../services/JobService";
import useErrorPage from "../../hooks/useErrorPage";
import { addCandidate } from "../../services/RecruiterService";
import { ResponseModel } from "../../models/ResponseModel";
import { SubmitFormText } from "../../helpers/LayoutTexts";
import { ReactComponent as CloseCircle } from "../../assets/svg/close-circle.svg";
import { ErrorIndicator } from "../Shared/Inputs/ErrorIndicator";
import { FileInput } from "../Shared/Inputs/FileInput";
import { Button } from "../Shared/Buttons/Button";
import { ReactComponent as SearchIcon } from '../../assets/svg/search.svg';
import { PopUpWindow } from '../../components/Shared/PopUpWindow';
import { InputValidation } from "../../models/InputValidation";
import { Candidate } from "../../models/Candidate";
import { UserType } from "../../enums/UserType";

interface Props {    
    open: boolean;
    onClose: () => void;
    onSuccess: (message: string) => void;
    onError: (message: string) => void;
    recruiter: LoggedRecruiter;
    styles: any;
}

type AddCandidateForm = Partial<Candidate> & {
    preferredRole: string;
    sectors: Sector[];
    candidateCV: string;
}

type AddCandidateValidation = {
    forename: InputValidation;
    surname: InputValidation; 
    email: InputValidation;
    recentJobTitle: InputValidation; 
    recentEmployer: InputValidation; 
    mobileNumber: InputValidation;
    address1: InputValidation; 
    preferredRole: InputValidation;     
    sectors: InputValidation;   
    candidateCV: InputValidation;
    [key: string]: InputValidation; 
}

const initialFormInput: AddCandidateForm = {
    forename: "",
    surname: "",
    email: "",
    recentJobTitle: "",
    recentEmployer: "",
    mobileNumber: "",
    address1: "",
    preferredRole: "",
    sectors: [],
    candidateCV: ""
}

const initialFormValidation: AddCandidateValidation = {
    forename: {},
    surname: {},
    email: {},
    recentJobTitle: {},
    recentEmployer: {},
    mobileNumber: {},
    address1: {},
    preferredRole: {},
    sectors: {},
    candidateCV: {}
}

export const AddCandidate: React.FC<Props> = (props) => {
    const [formModel, setFormModel] = useState<AddCandidateForm>(initialFormInput);
    const [validation, setValidation] = useState<AddCandidateValidation>(initialFormValidation);
    const [candidateCV, setCandidateCV] = useState<File>();
    const [sectors, setSectors] = useState<Sector[]>([]);
    const [hoveredSectorId, setHoveredSectorId] = useState<number>(0);
    const [showSectorDropdown, setShowSectorDropdown] = useState<boolean>(false);
    const [hoveredIndex, setHoveredIndex] = useState<number>(-1);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [sectorSearchKeyword, setSectorSearchKeyword] = useState<string>("");
    const recruiter = props.recruiter;
    const styles = props.styles;
    const [toErrorPage] = useErrorPage();

    useEffect(() => {
        if (props.open) {
            getAllSectorList();
        }
    }, [props.open]);

    const handleClose = () => {        
        setFormModel(initialFormInput);
        setValidation(initialFormValidation);
        setCandidateCV(undefined);        
        setSectorSearchKeyword("");
        setIsSubmitting(false);
        props.onClose();
    }

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const value = event.target.value;
        setFormModel((values: any) => ({ ...values, [name]: value }));
        if(validation[name] && validation[name].invalid) {
            validateInput(name, value);
        }
    }

    const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const value = event.target.value;
        validateInput(name, value);
    }

    const handleFileChange = (selectedFiles: FileList) => {
        if (selectedFiles.length > 0) {
            setCandidateCV(selectedFiles[0]);
            setFormModel((values: any) => ({ ...values, ["candidateCV"]: selectedFiles[0].name }));
            let cvValidation = validateCV(selectedFiles[0]);
            setValidation((values: any) => ({...values, ["candidateCV"]: cvValidation}));
        }
    } 

    const validateInput = (name: string, value: string) => {
        if(name === "forename" || name === "surname") {
            setValidation((values: any) => ({...values, [name]: validateName(name, value, 50)}));
        }
        if(name === "address1" && value !== "") {
            setValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
        }
        if(name === "preferredRole") {
            if(value === "") {
                setValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Preferred Role is required"}}));
            } 
            else {
                setValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
            }
        }
        if(name === "recentEmployer" && value !== "") {
            setValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
        }
        if(name === "recentJobTitle" && value !== "") {
            setValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
        }
        if(name === "email") {
            if(value === "") {
                setValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Email is required"}}));
            }
            else if (!validateEmail(value)) {
                setValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Invalid email address!"}}));
            }
            else {
                setValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
            }
        }
        if(name === "mobileNumber") {
            if(value === "") {
                setValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Mobile number is required"}}));
            }
            else if(!validatePhoneNumber(value)) {
                setValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Invalid Mobile number!"}}));
            }
            else {
                setValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
            }
        }
    }

    const validateForm = (): number => {
        let formValidation: AddCandidateValidation = {
            forename: {valid: true},
            surname: {valid: true},
            email: {valid: true},
            recentJobTitle: {valid: true},
            recentEmployer: {valid: true},
            mobileNumber: {valid: true},
            address1: {valid: true},
            preferredRole: {valid: true},
            sectors: {valid: true},
            candidateCV: {valid: true}
        }
        formValidation.forename = validateName("First Name", formModel.forename!, 50);
        formValidation.surname = validateName("Last Name", formModel.surname!, 50);
        if(formModel.email === "") {
            formValidation.email = {valid: false, invalid: true, invalidMessage: "Email is required"};
        }
        else if(!validateEmail(formModel.email!)) {
            formValidation.email = {valid: false, invalid: true, invalidMessage: "Invalid email address!"};
        }
        if (formModel.mobileNumber === "") {
            formValidation.mobileNumber = {valid: false, invalid: true, invalidMessage: "Mobile number is required"};
        }
        else if (!validatePhoneNumber(formModel.mobileNumber!)) {
            formValidation.mobileNumber  = {valid: false, invalid: true, invalidMessage: "Invalid Mobile number!"};
        }
        if(formModel.preferredRole === "") {
            formValidation.preferredRole = {valid: false, invalid: true, invalidMessage: "Preferred Role is required"};
        }
        if(!sectors.some(sector => sector.isSelected)){
            formValidation.sectors = {valid: false, invalid: true, invalidMessage: "Candidate sector is required"};
        }
        formValidation.candidateCV = validateCV(candidateCV);
        setValidation(formValidation);
        const validInputCount = Object.keys(formValidation).filter(key => formValidation[key].valid).length;
        return validInputCount;
    }

    const filteredSectors = useMemo(() => {
        const trimmed = sectorSearchKeyword.trim();
        if (!trimmed) return sectors;
        const query = trimmed.toLowerCase();
        return sectors.filter((sector) => {
            const sectorName = (sector.sectorName ?? '').toLowerCase();      
            return sectorName.includes(query);
        });
    }, [sectorSearchKeyword, sectors]);

    const toggleSector = (sectorId: number) => { 
        const updatedSectors = sectors.map(sector => 
            sector.sectorId === sectorId 
                ? { ...sector, isSelected: !sector.isSelected }
                : sector);
        setSectors(updatedSectors);
        const hasSelectedSectors = updatedSectors.some(sector => sector.isSelected);
        if(!hasSelectedSectors) {
            setValidation((values: any) => ({...values, sectors: {valid: false, invalid: true, invalidMessage: "Candidate sector is required"}}));
        }
        else {
            setValidation((values: any) => ({ ...values, sectors: {valid: true, invalid: false}}));
        }
    }

    const filterSectors = (keyword: string) => {
        setSectorSearchKeyword(keyword);
    }

    async function getAllSectorList() {
        await getAllSectors()
        .then((sectorList: Sector[]) => {
            setSectors(sectorList);
        })
        .catch((error) => {
            toErrorPage(error, UserType.Recruiter);
        });
    }

    async function handleSubmit () {        
        let validInputCount = Object.keys(validation).filter(key => validation[key].valid).length;
        if(validInputCount < 10){
            validInputCount = validateForm();
        }
        if(validInputCount === 10) {
            setIsSubmitting(true);
            const selectedSectors = sectors.filter(sector => sector.isSelected);
            const candidateData = {...formModel, sectors: selectedSectors};
            const formData = new FormData();
            formData.append("ModelString", JSON.stringify(candidateData));
            formData.append("Attachment", candidateCV!);
            await addCandidate(formData, recruiter!.recruiterId, recruiter!.token)
            .then((response: ResponseModel) => {
                if(response.status) {
                    props.onSuccess(response.message);
                    handleClose();
                }
                else {
                    handleClose();
                    props.onError(response.message);
                }
            })
            .catch((error) => {
                toErrorPage(error, UserType.Recruiter);
            });
        }
        else {
            props.onError(SubmitFormText.error);
        }
    }

    return (
        <PopUpWindow styles={styles.dialog} open={props.open} toggle={handleClose} closeCircle={true}>
            <div style={styles.content}>
                <span style={styles.dialogTitle}>{"Add to Candidate Pool"}</span>
                <div style={styles.row}>
                    <div style={styles.leftInputWrapper}>
                        <label style={styles.lable}>{"First Name *"}</label>
                        <TextInput
                            label={"First Name"}
                            styles={styles.rightInput}
                            inputType={'text'}
                            name={"forename"}
                            value={formModel.forename}
                            handleChange={handleInputChange}
                            handleBlur={handleInputBlur}
                            validation={validation.forename}
                        />
                    </div>
                    <div style={styles.rightInputWrapper}>
                        <label style={styles.lable}>{"Last Name *"}</label>
                        <TextInput
                            label={'Last Name'}
                            styles={styles.leftInput}
                            inputType={'text'}
                            name={"surname"}
                            value={formModel.surname}
                            handleChange={handleInputChange}
                            handleBlur={handleInputBlur}
                            validation={validation.surname}
                        />
                    </div>
                </div>
                <div style={styles.row}>
                    <div style={styles.leftInputWrapper}>
                        <label style={styles.lable}>{"Preferred Role *"}</label>
                        <TextInput
                            label={"Preferred Role"}
                            styles={styles.rightInput}
                            inputType={'text'}
                            name={"preferredRole"}
                            value={formModel.preferredRole}
                            handleChange={handleInputChange}
                            handleBlur={handleInputBlur}
                            validation={validation.preferredRole}
                        />
                    </div>
                    <div style={styles.rightInputWrapper}>
                        <label style={styles.lable}>{"Current Sector *"}</label>
                        <div 
                            style={{...styles.sectorContainer, ...(validation.sectors?.valid && styles.valid), ...(validation.sectors?.invalid && styles.invalid)}}
                            onMouseEnter={() => setShowSectorDropdown(true)}
                            onMouseLeave={() => setShowSectorDropdown(false)}
                            onClick={() => setShowSectorDropdown(!showSectorDropdown)}
                        >
                            {sectors.filter((sector) => sector.isSelected).map((sector) =>
                                <div 
                                    key={sector.sectorId}
                                    style={styles.sector}
                                    onMouseEnter={() => setHoveredSectorId(sector.sectorId)}
                                    onMouseLeave={() => setHoveredSectorId(0)}
                                >
                                    {sector.sectorName}
                                    {hoveredSectorId === sector.sectorId && <CloseCircle style={styles.closeIcon} onClick={() => toggleSector(sector.sectorId)} />}
                                </div>
                            )}
                            <div style={{ ...styles.sector, ...styles.allSectorWrapper }}>
                                + Add New
                                <div style={styles.hoverWrapper}>
                                    <div style={showSectorDropdown ? styles.hover : styles.leave}>
                                        <div onClick={e => {e.stopPropagation()}}>
                                            <input type="text" style={styles.currentSector.search} placeholder={"Search"} onChange={(e) => filterSectors(e.target.value)} />
                                            {sectorSearchKeyword === "" && <SearchIcon style={styles.currentSector.icon} />}
                                        </div>
                                        <div className="scrollArea" style={styles.scrollArea}>
                                            {filteredSectors.filter((sector) => !sector.isSelected).map((sector, index) => 
                                                <React.Fragment key={sector.sectorId}>
                                                    <div 
                                                        style={{...styles.option, ...(hoveredIndex === index && styles.option.hover)}}
                                                        onClick={() => toggleSector(sector.sectorId)} 
                                                        onMouseEnter={() => setHoveredIndex(index)} 
                                                        onMouseLeave={() => setHoveredIndex(-1)}
                                                    >
                                                        {sector.sectorName}
                                                    </div>
                                                    {index < filteredSectors.filter((s) => !s.isSelected).length -1 && 
                                                    <hr 
                                                        style={{ 
                                                            ...styles.bottomBorder, 
                                                            ...((hoveredIndex === index || hoveredIndex === index + 1 || sectors.length === index + 1) && styles.hoveredBorder) 
                                                        }}
                                                    />}   
                                                </React.Fragment>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {validation.sectors?.invalid && <ErrorIndicator invalidMessage={validation.sectors.invalidMessage!} styles={styles.inputError}/>}
                        </div>
                    </div>
                </div>
                <div style={styles.row}>
                    <div style={styles.leftInputWrapper}>
                        <label style={styles.lable}>{"Current Company"}</label>
                        <TextInput
                            label={'Current Company'}
                            styles={styles.rightInput}
                            inputType={'text'}
                            name={"recentEmployer"}
                            value={formModel.recentEmployer}
                            handleChange={handleInputChange}
                            handleBlur={handleInputBlur}
                            validation={validation.recentEmployer}
                        />
                    </div>
                    <div style={styles.rightInputWrapper}>
                        <label style={styles.lable}>{"Current Job Title"}</label>
                        <TextInput
                            label={'Current Job Title'}
                            styles={styles.leftInput}
                            inputType={'text'}
                            name={"recentJobTitle"}
                            value={formModel.recentJobTitle}
                            handleChange={handleInputChange}
                            handleBlur={handleInputBlur}
                            validation={validation.recentJobTitle}
                        />
                    </div>
                </div>
                <div>
                    <label style={styles.lable}>{"Address"}</label>
                    <TextInput
                        label={'Address'}
                        styles={styles.rightInput}
                        inputType={'text'}
                        name={"address1"}
                        value={formModel.address1}
                        handleChange={handleInputChange}
                        handleBlur={handleInputBlur}
                        validation={validation.address1}
                    />
                </div>
                <div style={styles.row}>
                    <div style={styles.leftInputWrapper}>
                        <label style={styles.lable}>{"Email *"}</label>
                        <TextInput
                            label={'Email'}
                            styles={styles.leftInput}
                            inputType={'text'}
                            name={"email"}
                            value={formModel.email}
                            handleChange={handleInputChange}
                            handleBlur={handleInputBlur}
                            validation={validation.email}
                        />
                    </div>
                    <div style={styles.rightInputWrapper}>
                        <label style={styles.lable}>{"Phone *"}</label>
                        <TextInput
                            label={'Mobile Number'}
                            styles={styles.rightInput}
                            inputType={'text'}
                            name={"mobileNumber"}
                            value={formModel.mobileNumber}
                            handleChange={handleInputChange}
                            handleBlur={handleInputBlur}
                            validation={validation.mobileNumber}
                        />
                    </div>
                </div>
                <div>
                    <label style={styles.lable}>{"CV *"}</label>
                    <FileInput
                        label={'CV'}
                        id={"candiate-cv"}
                        name={"candidateCV"}
                        selectedFileName={formModel.candidateCV}
                        styles={styles.cvUpload}
                        handleChange={(event) => handleFileChange(event!.currentTarget.files!)}
                        validation={validation.candidateCV}
                    />
                </div>
                <div style={styles.addPoolWrapper}>
                    <span style={styles.fileWarning}><span style={styles.fileWarning.bold}>{"Support files"}</span>{" : pdf, docx, doc | "}<span style={styles.fileWarning.bold}>{"Max file size"}</span>{" : 2MB"}</span>
                    <Button style={styles.addPool} onClick={handleSubmit} disabled={isSubmitting}>{"ADD TO POOL"}</Button>
                </div>
            </div>
        </PopUpWindow>
    )
}